import type { CardId, RunState, SkillCardId } from "./runState";
import { getSkillLevel, isSkillMaxed } from "./runState";
import { CARDS } from "../data/cards";
import type { PerkId } from "../data/perks";
import { createDeterministicRng } from "./simulationHelpers";
import { getAutomationOverride } from "../automation/automationTags";

export type RewardOffer =
  | { kind: "skill"; cardId: SkillCardId }
  | { kind: "perks"; cardIds: PerkId[] };

const CARD_ORDER: CardId[] = [
  "skill_attack",
  "skill_slash",
  "skill_defend",
  "perk_hp",
  "perk_atk",
  "perk_lifesteal",
];

const pickRandomWithRng = <T>(items: T[], excluded: Set<T>, rng: () => number): T | undefined => {
  const available = items.filter((item) => !excluded.has(item));
  if (available.length === 0) {
    return undefined;
  }
  const idx = Math.floor(rng() * available.length);
  return available[idx];
};

const rollRewardCardIdsInternal = (run: RunState, rng: () => number): CardId[] => {
  const debugReward = getAutomationOverride<string>("reward");
  if (debugReward === "skill" || debugReward === "chest") {
    // Force a skill reward (prefer one the player doesn't have or can upgrade)
    const skillCards = CARD_ORDER.filter((id) => CARDS[id].kind === "skill");
    const newSkillCards = skillCards.filter((cardId) => {
      const def = CARDS[cardId];
      return def.kind === "skill" && getSkillLevel(run, def.grantsSkill!) === 0;
    });
    const upgradeSkillCards = skillCards.filter((cardId) => {
      const def = CARDS[cardId];
      return def.kind === "skill" && !isSkillMaxed(run, def.grantsSkill!);
    });
    const preferred = newSkillCards.length > 0 ? newSkillCards : upgradeSkillCards;
    const pick = pickRandomWithRng(preferred, new Set(), rng) || preferred[0] || "skill_attack";
    return [pick];
  }
  if (debugReward === "perk" || debugReward === "perks") {
    // Force 3 perk rewards
    const perkCards = CARD_ORDER.filter((id) => CARDS[id].kind === "perk");
    const choices: CardId[] = [];
    const used = new Set<CardId>();
    while (choices.length < 3 && used.size < perkCards.length) {
      const pick = pickRandomWithRng(perkCards, used, rng);
      if (pick) {
        choices.push(pick);
        used.add(pick);
      }
    }
    return choices;
  }

  const choices: CardId[] = [];
  const used = new Set<CardId>();

  // Roll 1/3 chance for skill reward vs perk reward
  const isSkillReward = rng() < 1 / 3;

  if (isSkillReward) {
    // Skill reward: prefer offering a new skill; otherwise offer an upgrade.
    const newSkillCards = CARD_ORDER.filter((cardId) => {
      const def = CARDS[cardId];
      return def.kind === "skill" && getSkillLevel(run, def.grantsSkill!) === 0;
    });
    const upgradeSkillCards = CARD_ORDER.filter((cardId) => {
      const def = CARDS[cardId];
      return def.kind === "skill" && !isSkillMaxed(run, def.grantsSkill!);
    });
    const skillCards = newSkillCards.length > 0 ? newSkillCards : upgradeSkillCards;
    const pick = pickRandomWithRng(skillCards, used, rng);
    if (pick) {
      choices.push(pick);
    }

    // If no skills available, fall back to perks
    if (choices.length === 0) {
      while (choices.length < 3) {
        const perkPick = pickRandomWithRng(
          CARD_ORDER.filter((cardId) => CARDS[cardId].kind === "perk"),
          used,
          rng
        );
        if (!perkPick) {
          break;
        }
        choices.push(perkPick);
        used.add(perkPick);
      }
    }

    return choices;
  }

  // Perk reward: return 3 random perk cards
  while (choices.length < 3) {
    const pick = pickRandomWithRng(
      CARD_ORDER.filter((cardId) => CARDS[cardId].kind === "perk"),
      used,
      rng
    );
    if (!pick) {
      break;
    }
    choices.push(pick);
    used.add(pick);
  }

  return choices;
};

export const rollRewardCardIds = (run: RunState): CardId[] => {
  let rng = Math.random;
  const seed = getAutomationOverride<number | string>("seed");
  if (seed !== undefined) {
    rng = createDeterministicRng(typeof seed === "string" ? parseInt(seed, 10) : seed);
  }
  return rollRewardCardIdsInternal(run, rng);
};

export const rollRewardCardIdsWithRng = (run: RunState, rng: () => number): CardId[] =>
  rollRewardCardIdsInternal(run, rng);

export const rollRewardOffer = (run: RunState): RewardOffer => {
  let rng = Math.random;
  const seed = getAutomationOverride<number | string>("seed");
  if (seed !== undefined) {
    rng = createDeterministicRng(typeof seed === "string" ? parseInt(seed, 10) : seed);
  }
  const cardIds = rollRewardCardIdsInternal(run, rng);
  const first = cardIds[0];
  if (first && cardIds.length === 1 && CARDS[first].kind === "skill") {
    return { kind: "skill", cardId: first as SkillCardId };
  }
  return { kind: "perks", cardIds: cardIds as PerkId[] };
};

export const rollRewardOfferWithRng = (run: RunState, rng: () => number): RewardOffer => {
  const cardIds = rollRewardCardIdsWithRng(run, rng);
  const first = cardIds[0];
  if (first && cardIds.length === 1 && CARDS[first].kind === "skill") {
    return { kind: "skill", cardId: first as SkillCardId };
  }
  return { kind: "perks", cardIds: cardIds as PerkId[] };
};
